import React, { ReactElement, FunctionComponent, Suspense, memo, useCallback, useEffect, useMemo } from 'react';
import { Jute } from '..';
import { contentHasSound } from '../utils';
import { IEntityActionTypes, useActionStateStore } from '../zustand';
import { useDataStore } from '../zustand/data-store';
import { IUserActions, useUserStore } from '../zustand/user-store';
import ImageModal from './Modal/Image/ImageModal';
import VideoModal from './Modal/Video/VideoModal';
import Scene from './Scene/ArSceneContent';
import ZapparCanvas from './ZapparCanvas';
import ARRedirectButton from './buttons/ARRedirectButton/ARRedirectButton';
import SoundToggleButton from './buttons/SoundToggleButton/SoundToggleButton';
import { IImage, IRoot, ISceneComp, IVideo } from './r3f-components/component-data-structure';
import { IWebEmbedSettings, initialiseWebEmbed } from './r3f-components/component-data-structure/types/webEmbed';
import { hlsVideoCache } from './r3f-components/hooks';

export type IImageSceneDict = {[trackingImageId: string]: {id: string, content: ReactElement}[]};

export const Root: FunctionComponent = () => {
	const enlargedImgId = useUserStore((state) => state.enlargedImgId);
	const enlargedVideoId = useUserStore((state) => state.enlargedVideoId);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const webEmbed = useDataStore(({ webEmbed }) => webEmbed);
	const rootId = useDataStore(({ rootId }) => rootId);
	const isContentHasSound = contentHasSound(componentsById, rootId);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);

	// const { arButton } = useDataStore(({ webEmbed }) => webEmbed )|| initialiseWebEmbed();

	useEffect(() => {
		if (isContentHasSound && Jute.isIframe) {
			entityActionDispatch({
				type: IEntityActionTypes.MUTE_EXPERIENCE,
			});
		}
	},[isContentHasSound])

	const showArButton = (hasLoadedOtherData: boolean, webEmbed?: IWebEmbedSettings): boolean => {
		if (!webEmbed && hasLoadedOtherData) return !!initialiseWebEmbed().arButton;
		if (!webEmbed && !hasLoadedOtherData) return false;
		return webEmbed!.arButton ?? false;
	};

	// add scenes to canvas when their data is available
	const scenes = useMemo(() => {
		if (!rootId || !componentsById) return null;
		const { children } = componentsById[rootId] as IRoot;
		const imageTrackedScenes: IImageSceneDict = {};
		const worldTrackedScenes: ReactElement[] = [];
		const faceTrackedScenes: ReactElement[] = [];
		const content360Scenes: ReactElement[] = [];

		for (let i = 0; i < children.length; i++) {
			const sceneId = children[i];
			const scene = componentsById[sceneId] as ISceneComp;
			switch (scene.trackingType) {
				case 'image':
					if (typeof imageTrackedScenes[scene.trackingImageId] === 'undefined') imageTrackedScenes[(scene).trackingImageId] = [];
					imageTrackedScenes[scene.trackingImageId].push({id: sceneId, content: <Scene key={sceneId} id={sceneId} />});
					break;
				case 'world':
					worldTrackedScenes.push(<Scene key={sceneId} id={sceneId} />);
					break;
				case 'face':
					faceTrackedScenes.push(<Scene key={sceneId} id={sceneId} />);
					break;
				case 'content360':
					content360Scenes.push(<Scene key={sceneId} id={sceneId} />)
			}
		}
		return { imgTracked: imageTrackedScenes, wldTracked: worldTrackedScenes, faceTracked: faceTrackedScenes, content360: content360Scenes };
	}, [componentsById, rootId]);

	return <Suspense fallback={null}>
		{!!enlargedImgId && <ImageModal img={componentsById?.[enlargedImgId] as IImage} />}
		{!!enlargedVideoId && <VideoModal videoData={componentsById?.[enlargedVideoId] as IVideo} />}
		<ZapparCanvas>{scenes || null}</ZapparCanvas>
		{Jute.isIframe && (
			<>
				{isContentHasSound && Jute.isIframe && <SoundToggleButton />}
				{showArButton(!!componentsById, webEmbed) && <ARRedirectButton />}
			</>
		)}
	</Suspense>
};

Root.displayName = 'root';
export default memo(Root);
